/**
 * pods.ts — (#disperse) pure pod partitioning for natural crowd clustering.
 *
 * A co-located crowd is split into several small huddles ("pods") of ~4 (naturally 3–6) instead of
 * one giant ring. Kept DOM-free so the determinism core is unit-testable (npx tsx pods_test.mts).
 * scene.ts owns the actual placement (centroid + sunflower scatter); this owns only the maths of
 * HOW MANY pods and WHO lands together.
 */

/** Pod sizes for a crowd of n. ~4 per pod; first `extra` pods get one more. Sizes stay 3–6 for any
 *  realistic crowd (cap-limited venues never exceed ~18). n≤1 → no pods (drawn in place). */
export function podSizes(n: number): number[] {
    if (n <= 1) return n === 1 ? [1] : [];
    const P = Math.max(1, Math.round(n / 4));
    const base = Math.floor(n / P), extra = n % P;
    const out: number[] = [];
    for (let i = 0; i < P; i++) out.push(base + (i < extra ? 1 : 0));
    return out;
}

/** Order a crowd so a mutually-paired couple (each pointing at the other) sorts to the same key
 *  (the lower id) and thus lands adjacent → same pod. Deterministic, stable, no randomness. */
export function podOrder<T>(items: T[], idOf: (x: T) => number, partnerOf: (x: T) => number): T[] {
    const ids = new Set(items.map(idOf));
    const key = (x: T) => { const tid = partnerOf(x); return tid && ids.has(tid) ? Math.min(idOf(x), tid) : idOf(x); };
    return [...items].sort((a, b) => key(a) - key(b) || idOf(a) - idOf(b));
}
